import { RouteGroup, SingleRoute } from '@Constants/routes';
import { useTypedSelector } from '@Features/store';

/**
 * Platform-mode flags (admin-managed app settings). The platform can run
 * classic (classrooms only), courses-only, or hybrid, so every surface that
 * offers a product type has to ask before showing it.
 *
 * Bundles sit on top of standalone courses — a bundle is a group of them — so
 * they are only available when both flags are on. That rule already lives in
 * HeaderNavMenu; this hook is the single place it should be expressed.
 *
 * Note: instructor-wide scratch cards are valid in *every* mode and are
 * deliberately not gated by anything here.
 */
const usePlatformMode = () => {
	const {
		classrooms_enabled,
		standalone_courses_enabled,
		bundles_enabled,
		whatsapp_enabled,
		sms_enabled,
	} =
		useTypedSelector((state) => state.settings);

	const bundlesEnabled = standalone_courses_enabled && bundles_enabled;

	/**
	 * Whether a menu entry should be offered at all. Every navigation surface
	 * needs the same answer — the header nav and the side menu both list these
	 * routes — so the rule lives here rather than being restated per component.
	 */
	const isRouteEnabled = (route: SingleRoute | RouteGroup) => {
		if (route.requiresClassrooms) return classrooms_enabled;
		if (route.requiresStandalone) return standalone_courses_enabled;
		if (route.route === 'standalone-courses') return standalone_courses_enabled;
		if (route.route === 'bundles') return bundlesEnabled;
		return true;
	};

	return {
		classroomsEnabled: classrooms_enabled,
		standaloneEnabled: standalone_courses_enabled,
		bundlesEnabled,
		/** Master switch for exam-result WhatsApp notifications (Settings → WhatsApp). */
		whatsappEnabled: whatsapp_enabled,
		/** SMS channel readiness — greys the per-test SMS checkbox when false. */
		smsEnabled: sms_enabled,
		isRouteEnabled,
	};
};

export default usePlatformMode;
